import React, { useEffect, useState } from 'react';
import { CookieIcon, XIcon, CheckIcon } from 'lucide-react';
import { updateConsent } from '../utils/gtm-init';
const COOKIE_EXPIRY_DAYS = 365;
const COOKIE_KEY = 'cookie-consent';
const COOKIE_TIMESTAMP_KEY = 'cookie-consent-timestamp';
export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_KEY);
    const timestamp = localStorage.getItem(COOKIE_TIMESTAMP_KEY);
    if (consent && timestamp) {
      const consentDate = new Date(parseInt(timestamp));
      const now = new Date();
      const daysSinceConsent = (now.getTime() - consentDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceConsent > COOKIE_EXPIRY_DAYS) {
        localStorage.removeItem(COOKIE_KEY);
        localStorage.removeItem(COOKIE_TIMESTAMP_KEY);
        setupInteractionListeners();
      } else if (consent === 'accepted') {
        // Returning visitor: update consent only, no page_view here
        updateConsent(true, {
          sendInitialPageview: false
        });
      }
    } else {
      setupInteractionListeners();
    }
    function setupInteractionListeners() {
      const showConsent = () => {
        setIsVisible(true);
        window.removeEventListener('scroll', showConsent);
        window.removeEventListener('touchstart', showConsent);
        window.removeEventListener('click', showConsent);
        window.removeEventListener('keydown', showConsent);
      };
      window.addEventListener('scroll', showConsent, {
        once: true,
        passive: true
      });
      window.addEventListener('touchstart', showConsent, {
        once: true,
        passive: true
      });
      window.addEventListener('click', showConsent, {
        once: true
      });
      window.addEventListener('keydown', showConsent, {
        once: true
      });
      return () => {
        window.removeEventListener('scroll', showConsent);
        window.removeEventListener('touchstart', showConsent);
        window.removeEventListener('click', showConsent);
        window.removeEventListener('keydown', showConsent);
      };
    }
  }, []);
  const handleAccept = () => {
    localStorage.setItem(COOKIE_KEY, 'accepted');
    localStorage.setItem(COOKIE_TIMESTAMP_KEY, Date.now().toString());
    setIsVisible(false);

    // New acceptance: update consent AND send the first page_view immediately
    updateConsent(true, {
      sendInitialPageview: true
    });
  };
  const handleAcceptNecessary = () => {
    localStorage.setItem(COOKIE_KEY, 'necessary-only');
    localStorage.setItem(COOKIE_TIMESTAMP_KEY, Date.now().toString());
    setIsVisible(false);
  };
  if (!isVisible) return null;
  return <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" data-id="element-128" />
      {/* Cookie consent banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6" data-id="element-129">
        <div className="max-w-5xl mx-auto bg-white rounded-3xl shadow-2xl border border-slate-200" data-id="element-130">
          <div className="p-6 md:p-10" data-id="element-131">
            {/* Mobile View */}
            <div className="md:hidden" data-id="element-132">
              {!showDetails ? <div className="space-y-5" data-id="element-133">
                  <div className="flex items-start gap-4" data-id="element-134">
                    <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-3 rounded-2xl flex-shrink-0 shadow-lg" data-id="element-135">
                      <CookieIcon className="text-white" size={24} data-id="element-136" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-137">
                      <h3 className="text-lg font-bold text-slate-900 mb-2 tracking-tight" data-id="element-138">
                        We Value Your Privacy
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed" data-id="element-139">
                        We use cookies to enhance your experience and analyze
                        site traffic.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-3" data-id="element-140">
                    <button onClick={handleAccept} className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 rounded-2xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 font-bold text-base shadow-xl hover:shadow-blue-600/40 hover:scale-[1.02]" data-id="element-141">
                      Accept All Cookies
                    </button>
                    <button onClick={() => setShowDetails(true)} className="w-full bg-slate-100 text-slate-700 px-6 py-3 rounded-2xl hover:bg-slate-200 transition-all duration-300 font-semibold text-sm" data-id="element-142">
                      Customize Settings
                    </button>
                  </div>
                </div> : <div className="space-y-5" data-id="element-143">
                  <div className="flex items-start gap-4" data-id="element-144">
                    <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-3 rounded-2xl flex-shrink-0 shadow-lg" data-id="element-145">
                      <CookieIcon className="text-white" size={24} data-id="element-146" />
                    </div>
                    <div className="flex-1" data-id="element-147">
                      <h3 className="text-lg font-bold text-slate-900 mb-2 tracking-tight" data-id="element-148">
                        Cookie Preferences
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed" data-id="element-149">
                        Choose which cookies you want to accept. You can change
                        these settings anytime.
                      </p>
                    </div>
                    <button onClick={() => setShowDetails(false)} className="flex-shrink-0 p-2 hover:bg-slate-100 rounded-xl transition-colors" aria-label="Close details" data-id="element-150">
                      <XIcon className="text-slate-400" size={20} data-id="element-151" />
                    </button>
                  </div>
                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-4 space-y-3" data-id="element-152">
                    <div className="flex items-start gap-3" data-id="element-153">
                      <div className="bg-blue-600 p-1.5 rounded-lg flex-shrink-0 mt-0.5" data-id="element-154">
                        <CheckIcon className="text-white" size={14} data-id="element-155" />
                      </div>
                      <div data-id="element-156">
                        <p className="font-bold text-slate-900 text-sm mb-1" data-id="element-157">
                          Essential Cookies
                        </p>
                        <p className="text-slate-600 text-xs leading-relaxed" data-id="element-158">
                          Required for the website to function. Cannot be
                          disabled.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-159">
                      <div className="bg-blue-600 p-1.5 rounded-lg flex-shrink-0 mt-0.5" data-id="element-160">
                        <CheckIcon className="text-white" size={14} data-id="element-161" />
                      </div>
                      <div data-id="element-162">
                        <p className="font-bold text-slate-900 text-sm mb-1" data-id="element-163">
                          Analytics Cookies
                        </p>
                        <p className="text-slate-600 text-xs leading-relaxed" data-id="element-164">
                          Help us improve by understanding how you use our site.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-3" data-id="element-165">
                    <button onClick={handleAccept} className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 rounded-2xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 font-bold text-base shadow-xl hover:shadow-blue-600/40 hover:scale-[1.02]" data-id="element-166">
                      Accept All Cookies
                    </button>
                    <button onClick={handleAcceptNecessary} className="w-full bg-slate-100 text-slate-700 px-6 py-3 rounded-2xl hover:bg-slate-200 transition-all duration-300 font-semibold text-sm" data-id="element-167">
                      Essential Only
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 text-center leading-relaxed" data-id="element-168">
                    View our{' '}
                    <a href="/privacy-policy" className="text-blue-600 hover:text-blue-700 font-medium underline" data-id="element-169">
                      Privacy Policy
                    </a>
                  </p>
                </div>}
            </div>
            {/* Desktop View */}
            <div className="hidden md:block" data-id="element-170">
              <div className="flex items-start gap-6 mb-8" data-id="element-171">
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-4 rounded-2xl flex-shrink-0 shadow-lg" data-id="element-172">
                  <CookieIcon className="text-white" size={32} data-id="element-173" />
                </div>
                <div className="flex-1" data-id="element-174">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3 tracking-tight" data-id="element-175">
                    We Value Your Privacy
                  </h3>
                  <p className="text-slate-600 leading-relaxed text-base" data-id="element-176">
                    We use cookies to enhance your browsing experience, analyze
                    site traffic, and provide personalized content. By clicking
                    "Accept All", you consent to our use of cookies.
                  </p>
                </div>
              </div>
              {showDetails && <div className="mb-8 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-6" data-id="element-177">
                  <h4 className="font-bold text-slate-900 mb-4 text-lg" data-id="element-178">
                    Cookie Details
                  </h4>
                  <div className="space-y-4" data-id="element-179">
                    <div className="flex items-start gap-4" data-id="element-180">
                      <div className="bg-blue-600 p-2 rounded-xl flex-shrink-0 mt-0.5" data-id="element-181">
                        <CheckIcon className="text-white" size={18} data-id="element-182" />
                      </div>
                      <div data-id="element-183">
                        <p className="font-bold text-slate-900 mb-1" data-id="element-184">
                          Essential Cookies (Always Active)
                        </p>
                        <p className="text-slate-600 text-sm leading-relaxed" data-id="element-185">
                          These cookies are necessary for the website to
                          function and cannot be disabled. They enable core
                          functionality such as security, network management,
                          and accessibility.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4" data-id="element-186">
                      <div className="bg-blue-600 p-2 rounded-xl flex-shrink-0 mt-0.5" data-id="element-187">
                        <CheckIcon className="text-white" size={18} data-id="element-188" />
                      </div>
                      <div data-id="element-189">
                        <p className="font-bold text-slate-900 mb-1" data-id="element-190">
                          Analytics Cookies
                        </p>
                        <p className="text-slate-600 text-sm leading-relaxed" data-id="element-191">
                          These cookies help us understand how visitors interact
                          with our website by collecting and reporting
                          information anonymously. This helps us improve our
                          site.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>}
              <div className="flex items-center gap-4" data-id="element-192">
                <button onClick={handleAccept} className="flex-[2] bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-2xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 font-bold text-lg shadow-xl hover:shadow-blue-600/40 hover:scale-[1.02]" data-id="element-193">
                  Accept All Cookies
                </button>
                {showDetails ? <button onClick={handleAcceptNecessary} className="flex-1 bg-slate-100 text-slate-700 px-6 py-4 rounded-2xl hover:bg-slate-200 transition-all duration-300 font-semibold text-base" data-id="element-194">
                    Essential Only
                  </button> : <button onClick={() => setShowDetails(true)} className="flex-1 bg-slate-100 text-slate-700 px-6 py-4 rounded-2xl hover:bg-slate-200 transition-all duration-300 font-semibold text-base" data-id="element-195">
                    Customize Settings
                  </button>}
              </div>
              <p className="text-xs text-slate-500 mt-6 text-center leading-relaxed" data-id="element-196">
                By continuing to use our site, you agree to our{' '}
                <a href="/privacy-policy" className="text-blue-600 hover:text-blue-700 font-medium underline" data-id="element-197">
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a href="/cookie-policy" className="text-blue-600 hover:text-blue-700 font-medium underline" data-id="element-198">
                  Cookie Policy
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>;
}